import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  LeftArrow,
  RightArrow,
  Timeslot,
  Ul,
  Li,
  BoxNotSelected,
  BoxSelected,
  BoxBooked,
  SpinnerContainer
} from "./styles"
import Card from "../card"
import LoadingSpinner from "../loading-spinner"
import Button from "../button"
import { P } from "../typography"
import Moment from "../moment"
import Grid from "../grid"

const AvailabilityGrid = ({
  groupedTimeslots,
  selected,
  setSelected,
  handlePreviousWeek,
  handleNextWeek,
  timezone,
  gridArea,
  disable_timeslot,
  loaded,
  recurring,
  handleClearAvailability,
  showRecurringButton,
  showClearButton,
  wc,
  submitting
}) => {
  const [lastSelected, setLastSelected] = useState({ timestamp: null, group: null })
  const [dragging, setDragging] = useState(false)
  const [adding, setAdding] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const findSlotAndGroup = (timestamp) => {
    for (const group of groupedTimeslots) {
      const slot = group.timeslots.find((slot) => slot.timestamp === timestamp)
      if (slot) return { slot, group }
    }
    return { slot: null, group: null }
  }

  // Handle selection by Click+Drag
  const updateSelection = (startTimestamp, endTimestamp) => {
    const { slot: startSlot, group: startGroup } = findSlotAndGroup(startTimestamp)
    const { slot: endSlot, group: endGroup } = findSlotAndGroup(endTimestamp)

    if (startSlot && endSlot) {
      let newSelected = new Set(selected)
      const startGroupIndex = groupedTimeslots.findIndex((g) => g.date === startGroup.date)
      const endGroupIndex = groupedTimeslots.findIndex((g) => g.date === endGroup.date)

      // If the start and end timeslots are in the same group
      if (startGroupIndex === endGroupIndex) {
        const group = groupedTimeslots[startGroupIndex]
        const startIndex = Math.min(group.timeslots.indexOf(startSlot), group.timeslots.indexOf(endSlot))
        const endIndex = Math.max(group.timeslots.indexOf(startSlot), group.timeslots.indexOf(endSlot))

        // This checks what the first timeslot clicked was
        if (Math.abs(startIndex - endIndex) === 1) {
          // If first timeslot clicked was just de-selected - add timeslots, otherwise, delete
          if (selected.includes(lastSelected.timestamp)) {
            setAdding(false)
            setDeleting(true)
            newSelected.delete(lastSelected.timestamp)
          } else {
            setAdding(true)
            setDeleting(false)
            newSelected.add(lastSelected.timestamp)
          }
        }

        if (deleting) {
          for (let i = startIndex; i <= endIndex; i++) {
            const ts = group.timeslots[i].timestamp
            // If the selected timeslots are all re-selected, deselect them
            newSelected.delete(ts)

            // Ensure lastSelected is delselected if it is in the range
            if (newSelected.has(lastSelected.timestamp)) {
              newSelected.delete(lastSelected.timestamp)
            }
          }
        } else if (adding) {
          for (let i = startIndex; i <= endIndex; i++) {
            newSelected.add(group.timeslots[i].timestamp)
          }
        }
      } else {
        // Handle selection across multiple groups
        if (!adding && selected.includes(lastSelected.timestamp)) {
          setDeleting(true)
          setAdding(false)
        } else if (!deleting) {
          setAdding(true)
        }

        for (let i = startGroupIndex; i <= endGroupIndex; i++) {
          const group = groupedTimeslots[i]
          const startIndex =
            i === startGroupIndex ? group.timeslots.findIndex((ts) => ts.timestamp === lastSelected.timestamp) : 0
          const endIndex =
            i === endGroupIndex
              ? group.timeslots.findIndex((ts) => ts.timestamp === endTimestamp)
              : group.timeslots.length - 1

          for (let j = startIndex; j <= endIndex; j++) {
            const ts = group.timeslots[j].timestamp

            if (adding) {
              newSelected.add(ts)
            } else {
              newSelected.delete(ts)
            }
          }
        }
      }

      setSelected(Array.from(newSelected))
    }
  }

  // Handle selection by Shift+Click or one-click
  const toggleTimeslot = (event, timestamp) => {
    event.preventDefault()
    const shiftKey = event.shiftKey
    const { slot, group } = findSlotAndGroup(timestamp)

    if (shiftKey && lastSelected && lastSelected.timestamp !== null) {
      // If Shift is pressed, handle range selection
      const { slot: lastSlot, group: lastGroup } = findSlotAndGroup(lastSelected.timestamp)

      if (lastSlot && slot) {
        const startGroupIndex = groupedTimeslots.findIndex((g) => g.date === lastGroup.date)
        const endGroupIndex = groupedTimeslots.findIndex((g) => g.date === group.date)

        let newSelected = new Set(selected)

        if (startGroupIndex === endGroupIndex) {
          // Both timeslots are in the same group
          const currentGroup = groupedTimeslots[startGroupIndex]
          let startIndex = Math.min(currentGroup.timeslots.indexOf(lastSlot), currentGroup.timeslots.indexOf(slot))
          let endIndex = Math.max(currentGroup.timeslots.indexOf(lastSlot), currentGroup.timeslots.indexOf(slot))

          // If the selected timeslots are all re-selected, deselect them
          const allSelected = currentGroup.timeslots
            .slice(startIndex, endIndex + 1)
            .filter((ts) => ts.timestamp !== lastSelected.timestamp)
            .every((ts) => newSelected.has(ts.timestamp))

          for (let i = startIndex; i <= endIndex; i++) {
            const ts = currentGroup.timeslots[i].timestamp
            if (allSelected) {
              newSelected.delete(ts) // Deselect if already selected
            } else {
              newSelected.add(ts) // Select if not already selected
            }
          }
        } else {
          // Add timeslots if first selected was blank - delete timeslots if first and last was selected
          const endTimestampSelected = selected.includes(slot.timestamp)
          let adding = selected.includes(lastSelected.timestamp) || !endTimestampSelected
          // Handle selection across multiple groups
          for (let i = startGroupIndex; i <= endGroupIndex; i++) {
            const group = groupedTimeslots[i]
            const startIndex =
              i === startGroupIndex ? group.timeslots.findIndex((ts) => ts.timestamp === lastSelected.timestamp) : 0
            const endIndex =
              i === endGroupIndex
                ? group.timeslots.findIndex((ts) => ts.timestamp === timestamp)
                : group.timeslots.length - 1

            for (let j = startIndex; j <= endIndex; j++) {
              const ts = group.timeslots[j].timestamp
              if (adding) {
                newSelected.add(ts)
              } else {
                newSelected.delete(ts)
              }
            }
          }
        }

        setSelected(Array.from(newSelected))
      }
    } else {
      // Handle single selection (without Shift)
      if (isSelected(timestamp)) {
        setSelected(selected.filter((x) => x !== timestamp))
      } else {
        setSelected([...selected, timestamp])
      }
      setLastSelected({ timestamp, group })
    }
  }

  const handleMouseDown = (event, timestamp) => {
    event.preventDefault()
    if (!event.shiftKey) {
      const { group } = findSlotAndGroup(timestamp)
      setDragging(true)
      setLastSelected({ timestamp, group: group })
    }
  }

  const handleMouseUp = (event) => {
    event.preventDefault()
    if (dragging) {
      setDeleting(false)
      setAdding(false)
      setDragging(false)
      setLastSelected(null) // Reset lastSelected after dragging is complete
    }
  }

  const handleMouseEnter = (event, timestamp) => {
    if (dragging) {
      event.preventDefault()
      updateSelection(lastSelected.timestamp, timestamp)
    }
  }

  const previousWeek = (event) => {
    event.preventDefault()
    if (handlePreviousWeek !== undefined) handlePreviousWeek()
  }

  const nextWeek = (event) => {
    event.preventDefault()
    if (handleNextWeek !== undefined) handleNextWeek()
  }

  const isSelected = (timestamp) => selected.includes(timestamp)

  return (
    <Card variant="primary" gridArea={gridArea} id="availability_grid">
      {submitting && (
        <SpinnerContainer>
          <LoadingSpinner variant="default" />
          <span>Saving...</span>
        </SpinnerContainer>
      )}
      {!submitting && (
        <Grid>
          {!!loaded && (
            <Grid template="'left right' 1fr / 1fr" placeItems="baseline">
              <Ul>
                <Li>
                  <BoxNotSelected /> Not selected
                </Li>
                <Li>
                  <BoxSelected /> Selected
                </Li>
                {!recurring && (
                  <Li>
                    <BoxBooked /> Booked
                  </Li>
                )}
              </Ul>

              <Grid
                template="'left right' / 1fr auto"
                placeItems="end"
                gap={showClearButton && showRecurringButton ? "inherit" : 0}
              >
                {showRecurringButton && (
                  <Button href="/recurring-interview-availability" size="small">
                    Manage Recurring Availability
                  </Button>
                )}
                {showClearButton && (
                  <Button onClick={handleClearAvailability} size="small" variant="danger">
                    Clear All Availability
                  </Button>
                )}
              </Grid>
            </Grid>
          )}

          <Grid gap="1rem" grid="auto / auto-flow" placeContent={"space-between"}>
            {!recurring ? (
              <LeftArrow
                direction="left"
                onClick={previousWeek}
                disabled={handlePreviousWeek === undefined || wc < 1}
              />
            ) : (
              <div></div>
            )}

            {!!loaded &&
              groupedTimeslots.map(({ date, timeslots }) => (
                <Grid key={date} gap="0.5rem" placeContent="start center" placeItems="start center">
                  <P marginless bold align="center">
                    {recurring ? <Moment value={date} format="ddd" /> : <Moment value={date} format="ddd D MMM" />}
                  </P>
                  <Grid gap="0.25rem">
                    {timeslots.map(({ timestamp, restricted, booked, booked_elsewhere }) => (
                      <Timeslot
                        key={timestamp}
                        disabled={restricted || booked || booked_elsewhere || disable_timeslot}
                        selected={(!!isSelected(timestamp) || booked) && !restricted}
                        onClick={(event) => toggleTimeslot(event, timestamp)}
                        booked_slot={!!booked && !restricted}
                        booked_elsewhere_slot={!!booked_elsewhere && !restricted}
                        restricted_slot={!!restricted}
                        selected_slot={!!isSelected(timestamp) && !restricted}
                        onMouseDown={(event) => handleMouseDown(event, timestamp)}
                        onMouseUp={(event) => handleMouseUp(event, timestamp)}
                        onMouseEnter={(event) => handleMouseEnter(event, timestamp)}
                      >
                        <Moment value={timestamp} format="HH:mm" tz={timezone} />
                      </Timeslot>
                    ))}
                  </Grid>
                </Grid>
              ))}

            {!recurring ? (
              <RightArrow direction="right" onClick={nextWeek} disabled={handleNextWeek === undefined || wc >= 5} />
            ) : (
              <div></div>
            )}
          </Grid>
          {!loaded && (
            <Grid gap="0.5rem" placeContent="start center" placeItems="start center">
              <SpinnerContainer>
                <LoadingSpinner variant="default" />
                <span>Loading...</span>
              </SpinnerContainer>
            </Grid>
          )}
        </Grid>
      )}
    </Card>
  )
}

AvailabilityGrid.propTypes = {
  groupedTimeslots: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      timeslots: PropTypes.arrayOf(
        PropTypes.shape({
          timestamp: PropTypes.string.isRequired,
          restricted: PropTypes.bool.isRequired,
          booked: PropTypes.bool.isRequired
        })
      )
    })
  ),
  selected: PropTypes.array,
  setSelected: PropTypes.func.isRequired,
  handlePreviousWeek: PropTypes.func.isRequired,
  handleNextWeek: PropTypes.func.isRequired,
  timezone: PropTypes.string,
  gridArea: PropTypes.string,
  loaded: PropTypes.bool,
  recurring: PropTypes.bool,
  handleClearAvailability: PropTypes.func.isRequired,
  showRecurringButton: PropTypes.bool,
  showClearButton: PropTypes.bool,
  submitting: PropTypes.bool
}

AvailabilityGrid.defaultProps = {
  groupedTimeslots: [],
  selected: [],
  setSelected: undefined,
  handlePreviousWeek: undefined,
  handleNextWeek: undefined,
  timezone: null,
  gridArea: undefined,
  loaded: false,
  recurring: false,
  handleClearAvailability: undefined,
  showClearButton: true,
  showRecurringButton: false,
  submitting: false
}

export default AvailabilityGrid
